import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Puzzle, 
  Save, 
  Loader2, 
  Download,
  RefreshCw,
  CheckCircle,
  Server,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExtensionSettings() {
  const [serverUrl, setServerUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/extension`, {
        headers: getAuthHeaders()
      });
      setServerUrl(response.data.server_url || "");
    } catch (error) {
      console.error("Error fetching extension settings:", error);
      toast.error("Ошибка загрузки настроек");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/settings/extension`, { server_url: serverUrl }, {
        headers: getAuthHeaders()
      });
      toast.success("URL сервера сохранён!");
    } catch (error) {
      const message = error.response?.data?.detail || "Ошибка сохранения";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBuildDownload = async () => {
    if (!serverUrl) {
      toast.error("Сначала укажите и сохраните URL сервера");
      return;
    }
    
    setIsBuilding(true);
    try {
      const response = await axios.get(`${API}/build/extension`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sync-extension.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Расширение скачано!");
    } catch (error) {
      console.error("Build error:", error);
      toast.error("Ошибка сборки расширения");
    } finally {
      setIsBuilding(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="extension-settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Puzzle className="w-5 h-5 text-white" />
            </div>
            Расширение (Синхронизация)
          </h1>
          <p className="text-slate-500 mt-1">
            Настройка и скачивание браузерного расширения
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} data-testid="extension-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Обновить
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="extension-save-btn"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Сохранить
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Server URL Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Server className="w-5 h-5 text-blue-500" />
              URL сервера
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Адрес CRM сервера</Label>
              <Input
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="https://example.com"
                className="font-mono"
                data-testid="extension-server-url-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Этот URL будет встроен в расширение при сборке. Расширение будет отправлять данные на этот сервер.
              </p>
            </div>

            {serverUrl && (
              <div className="p-3 bg-green-50 rounded-lg flex items-center gap-2 text-green-700">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">URL настроен: <code className="bg-green-100 px-1 rounded">{serverUrl}</code></span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Download & Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Download className="w-5 h-5 text-green-500" />
              Скачать расширение
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg text-blue-800">
              <div className="font-medium mb-2">Как установить:</div>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                <li>Укажите URL сервера и сохраните</li>
                <li>Нажмите "Скачать расширение"</li>
                <li>Распакуйте ZIP-архив</li>
                <li>Откройте <code className="bg-blue-100 px-1 rounded">chrome://extensions</code></li>
                <li>Включите "Режим разработчика"</li>
                <li>Нажмите "Загрузить распакованное расширение"</li>
                <li>Выберите распакованную папку</li>
              </ol>
            </div>

            <Button 
              onClick={handleBuildDownload}
              disabled={isBuilding || !serverUrl}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="extension-download-btn"
            >
              {isBuilding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Скачать расширение
            </Button>

            <div className="p-4 bg-amber-50 rounded-lg text-amber-800">
              <div className="font-medium mb-2 flex items-center gap-1">
                <Info className="w-4 h-4" />
                Важно
              </div>
              <p className="text-sm">
                URL сервера встраивается в расширение при скачивании. 
                Если вы меняете URL — скачайте расширение заново и переустановите.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
